import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, AFFILIATE_ROUTES } from '@dukene/shared';
import {
  LayoutDashboard,
  Link2,
  DollarSign,
  BarChart3,
  Wallet,
  User,
  Menu,
  LogOut,
  Store,
  ChevronRight,
} from 'lucide-react';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
}

const navigation: NavItem[] = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: AFFILIATE_ROUTES.DASHBOARD,
  },
  {
    label: 'Affiliate Links',
    icon: Link2,
    href: AFFILIATE_ROUTES.LINKS,
  },
  {
    label: 'Commissions',
    icon: DollarSign,
    href: AFFILIATE_ROUTES.COMMISSIONS,
  },
  {
    label: 'Analytics',
    icon: BarChart3,
    href: AFFILIATE_ROUTES.ANALYTICS,
  },
  {
    label: 'Payouts',
    icon: Wallet,
    href: AFFILIATE_ROUTES.PAYOUTS,
  },
  {
    label: 'Profile',
    icon: User,
    href: AFFILIATE_ROUTES.PROFILE,
  },
];

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="h-screen bg-[#F6F6F7] flex overflow-hidden font-sans text-[#202223]">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#F1F1F1] border-r border-[#D2D5D9] transition-transform duration-300 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } flex flex-col`}
      >
        {/* Logo Section */}
        <div className="p-3 border-b border-[#D2D5D9] bg-white/50 backdrop-blur-sm">
          <div className="flex items-center gap-3 p-2 rounded-lg transition-all group border border-transparent">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
              <Store className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <h1 className="text-[13px] font-bold text-[#202223] truncate leading-tight uppercase tracking-tight">
                Dukene
              </h1>
              <p className="text-[10px] text-[#5C5F62] font-semibold flex items-center gap-1 uppercase tracking-wider mt-0.5">
                Affiliate Dashboard
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center justify-between px-3 py-1.5 rounded-md text-[14px] leading-6 transition-colors ${active
                  ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
                  : 'text-[#202223] hover:bg-[#EBEBEB]'
                  }`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-[20px] h-[20px] ${active ? 'text-emerald-600' : 'text-[#5C5F62]'}`} />
                  <span>{item.label}</span>
                </div>
                {active && <ChevronRight className="w-4 h-4 text-[#5C5F62]" />}
              </Link>
            );
          })}
        </nav>

        {/* User & Footer */}
        <div className="p-3 border-t border-[#D2D5D9] bg-white/20 space-y-2">
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-8 h-8 rounded bg-[#E1E4E8] flex items-center justify-center font-bold text-[#202223] text-xs flex-shrink-0">
                {user?.firstName?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-[#202223]">
                  {user?.firstName || 'Affiliate'}
                </p>
                <p className="text-[10px] text-[#5C5F62] truncate font-medium uppercase tracking-tighter">
                  {user?.role || 'AFFILIATE'}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[14px] text-[#5C5F62] hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#F6F6F7] relative h-screen">
        {/* Header */}
        <header className="h-[48px] bg-white border-b border-[#D2D5D9] flex items-center gap-4 px-4 lg:px-8 flex-shrink-0 z-40 sticky top-0">
          <button
            className="lg:hidden p-2 text-[#5C5F62] hover:bg-gray-100 rounded-md transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1 flex items-center">
            <h2 className="text-sm font-bold text-[#202223]">
              {navigation.find(item => isActive(item.href))?.label || 'Dashboard'}
            </h2>
          </div>
        </header>

        {/* Scrollable Content Container */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
          <div className="max-w-[1200px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
